import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Logger } from 'src/utils/Logger';
import { ApiError } from 'src/error/ApiError';
import PreShareWarningModal from './PreShareWarningModal';
import useShareTenantSkill from './hooks/useShareTenantSkill';

type ShareSkillButtonProps = {
  skillId: string;
  skillLabel: string;
};

export default function ShareSkillButton({ skillId, skillLabel }: ShareSkillButtonProps) {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutateAsync, isPending } = useShareTenantSkill();

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      await mutateAsync(skillId);
      setIsWarningOpen(false);
      notifications.show({ message: `Shared '${skillLabel}' with everyone`, color: 'green' });
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'An error occurred';
      setErrorMessage(message);
      // The modal stays open on failure, so the inline alert is the one place the user reads
      // this — a toast as well would be the same sentence twice. A 409 is the API refusing on
      // purpose (a re-share unchanged from the library), so it is a warning rather than a
      // fault worth an exception in Sentry.
      if (error instanceof ApiError && error.status === 409) {
        Logger.warn(`Could not share skill: ${message}`);
      } else {
        Logger.error(`Could not share skill: ${message}`, error);
      }
    }
  };

  return (
    <>
      <Button
        variant="default"
        size="compact-sm"
        aria-label={`Share ${skillLabel}`}
        onClick={() => setIsWarningOpen(true)}
      >
        Share
      </Button>
      <PreShareWarningModal
        opened={isWarningOpen}
        skillLabel={skillLabel}
        isSharing={isPending}
        errorMessage={errorMessage}
        onClose={() => {
          setErrorMessage(null);
          setIsWarningOpen(false);
        }}
        onConfirm={handleConfirm}
      />
    </>
  );
}
